/**
 * Design-system recipe showcase — the Tahoe CVA approval gate.
 *
 * Renders every shared UI primitive × variant × size × state so the new Tahoe
 * recipes (capsule buttons/inputs, 24px menus, ToggleSwitch, Slider) and the
 * lighter glass tokens can be judged in the real token environment.
 *
 * Three top-bar mode toggles drive the html-level keys the app uses:
 *   - light/dark  → html.light / html.dark
 *   - clear glass → html.clear + data-desktop-glass / data-desktop-native-glass
 *
 * Workshop rules: imports ONLY from @/components/ui and @/lib/cn. Dummy data
 * and local useState only — no api/stores/hooks.
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  ButtonSection,
  BadgeSection,
  CardSection,
  InputSection,
} from "./recipes-controls";
import {
  DropdownMenuSection,
  ContextMenuSection,
  ConfirmDialogSection,
  ToggleSwitchSection,
  SliderSection,
} from "./recipes-sections";

type Theme = "dark" | "light";

/**
 * Drives the html-level keys the app reads for theme + glass.
 *
 * Restores the previous html state on unmount so leaving the page doesn't
 * leak theme/glass classes into other workshop pages.
 */
function useRecipeEnvironment(theme: Theme, clear: boolean) {
  useEffect(() => {
    const root = document.documentElement;
    const prevClasses = root.className;
    const prev = {
      glass: root.dataset.desktopGlass,
      nativeGlass: root.dataset.desktopNativeGlass,
      workspace: root.dataset.desktopWorkspace,
    };

    // Theme + clear classes (the app keys off html.light / html.dark / html.clear)
    root.classList.remove("dark", "light", "clear");
    root.classList.add(theme);
    if (clear) root.classList.add("clear");

    // Glass attributes — only clear mode needs the desktop glass key; setting
    // data-desktop-native-glass in a browser zeroes fills/canvas (the OS would
    // paint the glass), which blacks out plain light/dark preview.
    if (clear) {
      root.dataset.desktopGlass = "true";
    } else {
      delete root.dataset.desktopGlass;
    }
    delete root.dataset.desktopNativeGlass;
    delete root.dataset.desktopWorkspace;

    return () => {
      root.className = prevClasses;
      if (prev.glass === undefined) delete root.dataset.desktopGlass;
      else root.dataset.desktopGlass = prev.glass;
      if (prev.nativeGlass === undefined) delete root.dataset.desktopNativeGlass;
      else root.dataset.desktopNativeGlass = prev.nativeGlass;
      if (prev.workspace === undefined) delete root.dataset.desktopWorkspace;
      else root.dataset.desktopWorkspace = prev.workspace;
    };
  }, [theme, clear]);
}

function ModeToggle({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant={active ? "default" : "secondary"}
      size="sm"
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </Button>
  );
}

export function RecipesPage() {
  const [theme, setTheme] = useState<Theme>("dark");
  const [clear, setClear] = useState(false);

  useRecipeEnvironment(theme, clear);

  return (
    <div className="p-8" data-testid="design-system-recipes-page">
      {/* Top bar */}
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-bold text-primary">Recipe Showcase</h1>
          <p className="mt-1 max-w-xl text-sm text-muted">
            Every shared UI primitive × variant × size × state, rendered in the
            new Tahoe recipes. Use this as the approval gate before porting the
            restyle. Note: true native glass renders only in the desktop app —
            here clear mode uses the CSS fallback.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ModeToggle
            active={theme === "light"}
            onClick={() => setTheme("light")}
            testId="recipe-mode-light"
          >
            Light
          </ModeToggle>
          <ModeToggle
            active={theme === "dark"}
            onClick={() => setTheme("dark")}
            testId="recipe-mode-dark"
          >
            Dark
          </ModeToggle>
          <ModeToggle
            active={clear}
            onClick={() => setClear((c) => !c)}
            testId="recipe-mode-clear"
          >
            <span className={cn(clear && "font-semibold")}>Clear glass</span>
          </ModeToggle>
        </div>
      </div>

      <ButtonSection />
      <BadgeSection />
      <CardSection />
      <InputSection />
      <DropdownMenuSection />
      <ContextMenuSection />
      <ConfirmDialogSection />
      <ToggleSwitchSection />
      <SliderSection />
    </div>
  );
}
